"use client";

import { c as _c } from "react/compiler-runtime";
import { UserPageRepPropsRepRates } from "@/app/[user]/rep/page";
import { ApiProfileRepRatesState, ProfileActivityLog, RatingWithProfileInfoAndLevel } from "@/entities/IProfile";
import { RateMatter } from "@/enums";
import { ApiDrop } from "@/generated/models/ApiDrop";
import { ApiFeedItemType } from "@/generated/models/ApiFeedItemType";
import { ApiProfileProxy } from "@/generated/models/ApiProfileProxy";
import { ApiWave } from "@/generated/models/ApiWave";
import { ApiWaveDropsFeed } from "@/generated/models/ApiWaveDropsFeed";
import { ApiIdentity } from "@/generated/models/ObjectSerializer";
import { wait } from "@/helpers/Helpers";
import { convertActivityLogParams } from "@/helpers/profile-logs.helpers";
import { Time } from "@/helpers/time";
import { CountlessPage, Page } from "@/helpers/Types";
import { useQueryKeyListener } from "@/hooks/useQueryKeyListener";
import { TypedFeedItem } from "@/types/feed.types";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { createContext, useMemo } from "react";
import { ActivityLogParams } from "../profile-activity/ProfileActivityLogs";
import { ProfileRatersParams } from "../user/utils/raters-table/wrapper/ProfileRatersTableWrapper";
import { addDropToDrops } from "./utils/addDropsToDrops";
import { increaseWavesOverviewDropsCount } from "./utils/increaseWavesOverviewDropsCount";
import { WAVE_DROPS_PARAMS, WAVE_FOLLOWING_WAVES_PARAMS } from "./utils/query-utils";
import { toggleWaveFollowing } from "./utils/toggleWaveFollowing";
export enum QueryKey {
  PROFILE = "PROFILE",
  PROFILE_LOGS = "PROFILE_LOGS",
  PROFILE_RATER_CIC_STATE = "PROFILE_RATER_CIC_STATE",
  PROFILE_RATERS = "PROFILE_RATERS",
  PROFILE_CIC_STATEMENTS = "PROFILE_CIC_STATEMENTS",
  PROFILE_SEARCH = "PROFILE_SEARCH",
  PROFILE_REP_RATINGS = "PROFILE_REP_RATINGS",
  PROFILE_TRANSACTIONS = "PROFILE_TRANSACTIONS",
  PROFILE_DISTRIBUTIONS = "PROFILE_DISTRIBUTIONS",
  TDH_GRANTS = "TDH_GRANTS",
  TDH_GRANT_TOKENS = "TDH_GRANT_TOKENS",
  PROFILE_CONSOLIDATED_TDH = "PROFILE_CONSOLIDATED_TDH",
  PROFILE_COLLECTED = "PROFILE_COLLECTED",
  PROFILE_COLLECTED_TRANSFER = "PROFILE_COLLECTED_TRANSFER",
  PROFILE_DROPS = "PROFILE_DROPS",
  XTDH_TOKENS = "XTDH_TOKENS",
  XTDH_TOKEN_CONTRIBUTORS = "XTDH_TOKEN_CONTRIBUTORS",
  XTDH_RECEIVED_NFTS = "XTDH_RECEIVED_NFTS",
  IDENTITY_TDH_STATS = "IDENTITY_TDH_STATS",
  GLOBAL_TDH_STATS = "GLOBAL_TDH_STATS",
  IDENTITY_AVAILABLE_CREDIT = "IDENTITY_AVAILABLE_CREDIT",
  IDENTITY_FOLLOWING_ACTIONS = "IDENTITY_FOLLOWING_ACTIONS",
  IDENTITY_FOLLOWERS = "IDENTITY_FOLLOWERS",
  IDENTITY_NOTIFICATIONS = "IDENTITY_NOTIFICATIONS",
  IDENTITY_SEARCH = "IDENTITY_SEARCH",
  WALLET_TDH_HISTORY = "WALLET_TDH_HISTORY",
  REP_CATEGORIES_SEARCH = "REP_CATEGORIES_SEARCH",
  MEMES_LITE = "MEMES_LITE",
  MEMELAB_LITE = "MEMELAB_LITE",
  WALLET_CONSOLIDATIONS_CHECK = "WALLET_CONSOLIDATIONS_CHECK",
  NEXTGEN_COLLECTIONS = "NEXTGEN_COLLECTIONS",
  COMMUNITY_MEMBERS_TOP = "COMMUNITY_MEMBERS_TOP",
  RESERVOIR_NFT = "RESERVOIR_NFT",
  DROPS = "DROPS",
  DROPS_LEADERBOARD = "DROPS_LEADERBOARD",
  DROP = "DROP",
  DROP_DISCUSSION = "DROP_DISCUSSION",
  GROUPS = "GROUPS",
  GROUPS_INFINITE = "GROUPS_INFINITE",
  GROUP = "GROUP",
  GROUP_WALLET_GROUP_WALLETS = "GROUP_WALLET_GROUP_WALLETS",
  NFTS_SEARCH = "NFTS_SEARCH",
  NFTS = "NFTS",
  NFT_COLLECTION_SEARCH = "NFT_COLLECTION_SEARCH",
  NFT_CONTRACT_OVERVIEW = "NFT_CONTRACT_OVERVIEW",
  NFT_TOKEN_METADATA = "NFT_TOKEN_METADATA",
  PROFILE_PROXY = "PROFILE_PROXY",
  PROFILE_PROFILE_PROXIES = "PROFILE_PROFILE_PROXIES",
  EMMA_IDENTITY_ALLOWLISTS = "EMMA_IDENTITY_ALLOWLISTS",
  EMMA_ALLOWLIST_RESULT = "EMMA_ALLOWLIST_RESULT",
  WAVES_OVERVIEW = "WAVES_OVERVIEW",
  WAVES_OVERVIEW_PUBLIC = "WAVES_OVERVIEW_PUBLIC",
  WAVES = "WAVES",
  WAVES_PUBLIC = "WAVES_PUBLIC",
  WAVE = "WAVE",
  WAVE_LOGS = "WAVE_LOGS",
  WAVE_VOTERS = "WAVE_VOTERS",
  WAVE_FOLLOWERS = "WAVE_FOLLOWERS",
  FEED_ITEMS = "FEED_ITEMS",
  WAVE_DECISIONS = "WAVE_DECISIONS",
  WAVE_OUTCOMES = "WAVE_OUTCOMES",
  WAVE_OUTCOME_DISTRIBUTION = "WAVE_OUTCOME_DISTRIBUTION",
  WAVE_OUTCOME_DISTRIBUTION_PAGE = "WAVE_OUTCOME_DISTRIBUTION_PAGE",
}
interface InitProfileRatersParamsAndData {
  readonly data: Page<RatingWithProfileInfoAndLevel>;
  readonly params: ProfileRatersParams;
}
interface InitProfileActivityLogsParams {
  readonly params: ActivityLogParams;
  readonly data: CountlessPage<ProfileActivityLog>;
}
interface InitProfileRepPageParams {
  readonly profile: ApiIdentity;
  readonly repRates: UserPageRepPropsRepRates;
  readonly repLogs: InitProfileActivityLogsParams;
  readonly repGivenToUsers: InitProfileRatersParamsAndData;
  readonly repReceivedFromUsers: InitProfileRatersParamsAndData;
  readonly handleOrWallet: string;
}
interface InitProfileIdentityPageParams {
  readonly profile: ApiIdentity;
  readonly activityLogs: InitProfileActivityLogsParams;
  readonly cicGivenToUsers: InitProfileRatersParamsAndData;
  readonly cicReceivedFromUsers: InitProfileRatersParamsAndData;
}
type ReactQueryWrapperContextType = {
  readonly setProfile: (profile: ApiIdentity) => void;
  readonly setWave: (wave: ApiWave) => void;
  readonly setWavesOverviewPage: (wavesOverview: ApiWave[]) => void;
  readonly setWaveDrops: (params: {
    readonly waveDrops: ApiWaveDropsFeed;
    readonly waveId: string;
  }) => void;
  readonly setProfileProxy: (profileProxy: ApiProfileProxy) => void;
  readonly onProfileProxyModify: ({
    profileProxyId,
    createdByHandle,
    grantedToHandle
  }: {
    readonly profileProxyId: string;
    readonly createdByHandle: string;
    readonly grantedToHandle: string;
  }) => void;
  onProfileCICModify: (params: {
    readonly targetProfile: ApiIdentity;
    readonly connectedProfile: ApiIdentity | null;
    readonly rater: string | null;
    readonly profileProxy: ApiProfileProxy | null;
  }) => void;
  onProfileRepModify: ({
    targetProfile,
    connectedProfile,
    profileProxy
  }: {
    readonly targetProfile: ApiIdentity;
    readonly connectedProfile: ApiIdentity | null;
    readonly profileProxy: ApiProfileProxy | null;
  }) => void;
  onProfileEdit: ({
    profile,
    previousProfile
  }: {
    readonly profile: ApiIdentity;
    readonly previousProfile: ApiIdentity | null;
  }) => void;
  onProfileStatementAdd: (params: {
    profile: ApiIdentity;
  }) => void;
  onProfileStatementRemove: (params: {
    profile: ApiIdentity;
  }) => void;
  onIdentityFollowChange: () => void;
  initProfileRepPage: (params: InitProfileRepPageParams) => void;
  initProfileIdentityPage: (params: InitProfileIdentityPageParams) => void;
  initCommunityActivityPage: ({
    activityLogs
  }: {
    activityLogs: InitProfileActivityLogsParams;
  }) => void;
  waitAndInvalidateDrops: () => void;
  addOptimisticDrop: (params: {
    readonly drop: ApiDrop;
  }) => void;
  readonly invalidateDrops: () => void;
  onGroupRemoved: ({
    groupId
  }: {
    readonly groupId: string;
  }) => void;
  onGroupChanged: ({
    groupId
  }: {
    readonly groupId: string;
  }) => void;
  onGroupCreate: () => void;
  onIdentityBulkRate: () => void;
  onWaveCreated: () => void;
  onWaveFollowChange: (param: {
    readonly waveId: string;
    following: boolean;
  }) => void;
  invalidateAll: () => void;
  invalidateNotifications: () => void;
  invalidateIdentityTdhStats: (params: {
    identity: string;
  }) => void;
};
export const ReactQueryWrapperContext = createContext<ReactQueryWrapperContextType>({
  setProfile: () => {},
  setWavesOverviewPage: () => {},
  setProfileProxy: () => {},
  setWave: () => {},
  setWaveDrops: () => {},
  onProfileProxyModify: () => {},
  onProfileCICModify: () => {},
  onProfileRepModify: () => {},
  onProfileEdit: () => {},
  onProfileStatementAdd: () => {},
  onProfileStatementRemove: () => {},
  onIdentityFollowChange: () => {},
  initProfileRepPage: () => {},
  initProfileIdentityPage: () => {},
  initCommunityActivityPage: () => {},
  waitAndInvalidateDrops: () => {},
  addOptimisticDrop: () => {},
  invalidateDrops: () => {},
  onGroupRemoved: () => {},
  onGroupChanged: () => {},
  onGroupCreate: () => {},
  onIdentityBulkRate: () => {},
  onWaveCreated: () => {},
  onWaveFollowChange: () => {},
  invalidateAll: () => {},
  invalidateNotifications: () => {},
  invalidateIdentityTdhStats: () => {}
});
export default function ReactQueryWrapper(t0) {
  const $ = _c(141);
  const {
    children
  } = t0;
  const queryClient = useQueryClient();
  const getHandlesFromProfile = _temp;
  let t1;
  if ($[0] !== queryClient) {
    t1 = t2 => {
      const {
        key,
        values
      } = t2;
      for (const value of values) {
        queryClient.invalidateQueries({
          queryKey: [key, value]
        });
      }
    };
    $[0] = queryClient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const invalidateQueries = t1;
  let t2;
  if ($[2] !== invalidateQueries) {
    t2 = profile_0 => {
      const handles_0 = getHandlesFromProfile(profile_0);
      invalidateQueries({
        key: QueryKey.PROFILE,
        values: handles_0
      });
    };
    $[2] = invalidateQueries;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const invalidateProfile = t2;
  let t3;
  if ($[4] !== queryClient) {
    t3 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_LOGS]
      });
    };
    $[4] = queryClient;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const invalidateLogs = t3;
  let t4;
  if ($[6] !== queryClient) {
    t4 = profile_1 => {
      const handles_1 = getHandlesFromProfile(profile_1);
      for (const handle of handles_1) {
        queryClient.setQueryData([QueryKey.PROFILE, handle], profile_1);
      }
    };
    $[6] = queryClient;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setProfile = t4;
  let t5;
  if ($[8] !== queryClient) {
    t5 = wave => {
      queryClient.setQueryData([QueryKey.WAVE, {
        wave_id: wave.id
      }], wave);
    };
    $[8] = queryClient;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const setWave = t5;
  let t6;
  if ($[10] !== queryClient) {
    t6 = wavesOverview => {
      const queryKey = [QueryKey.WAVES_OVERVIEW, {
        limit: WAVE_FOLLOWING_WAVES_PARAMS.limit,
        type: WAVE_FOLLOWING_WAVES_PARAMS.initialWavesOverviewType,
        only_waves_followed_by_authenticated_user: WAVE_FOLLOWING_WAVES_PARAMS.only_waves_followed_by_authenticated_user
      }];
      const existingData = queryClient.getQueryData(queryKey);
      if (existingData) {
        return;
      } else {
        queryClient.setQueryData(queryKey, {
          pages: [wavesOverview],
          pageParams: [undefined]
        });
      }
    };
    $[10] = queryClient;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const setWavesOverviewPage = t6;
  let t7;
  if ($[12] !== queryClient) {
    t7 = t8 => {
      const {
        waveDrops,
        waveId
      } = t8;
      const queryKey_0 = [QueryKey.DROPS, {
        waveId,
        limit: WAVE_DROPS_PARAMS.limit,
        dropId: null
      }];
      const existingData_0 = queryClient.getQueryData(queryKey_0);
      if (existingData_0) {
        return;
      } else {
        queryClient.setQueryData(queryKey_0, {
          pages: [waveDrops],
          pageParams: [undefined]
        });
      }
    };
    $[12] = queryClient;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const setWaveDrops = t7;
  let t8;
  if ($[14] !== queryClient) {
    t8 = profileProxy => {
      queryClient.setQueryData([QueryKey.PROFILE_PROXY, {
        id: profileProxy.id
      }], profileProxy);
    };
    $[14] = queryClient;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const setProfileProxy = t8;
  let t9;
  if ($[16] !== queryClient) {
    t9 = t10 => {
      const {
        profileProxyId
      } = t10;
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_PROXY, {
          id: profileProxyId
        }]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_PROFILE_PROXIES]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_PROFILE_PROXIES]
      });
    };
    $[16] = queryClient;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const onProfileProxyModify = t9;
  let t10;
  if ($[18] !== invalidateQueries) {
    t10 = t11 => {
      const {
        profile: profile_2,
        rater
      } = t11;
      const handles_2 = getHandlesFromProfile(profile_2);
      invalidateQueries({
        key: QueryKey.PROFILE_RATER_CIC_STATE,
        values: handles_2.map(h => ({
          handle: h,
          rater
        }))
      });
    };
    $[18] = invalidateQueries;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const invalidateProfileRaterCICState = t10;
  let t11;
  if ($[20] !== invalidateQueries) {
    t11 = profile_3 => {
      const handles_3 = getHandlesFromProfile(profile_3);
      invalidateQueries({
        key: QueryKey.PROFILE_CIC_STATEMENTS,
        values: handles_3
      });
    };
    $[20] = invalidateQueries;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  const invalidateProfileCICStatements = t11;
  let t12;
  if ($[22] !== queryClient) {
    t12 = t13 => {
      const {
        groupId
      } = t13;
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GROUPS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GROUP, groupId]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_LOGS, {
          groupId
        }]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.COMMUNITY_MEMBERS_TOP, {
          groupId
        }]
      });
    };
    $[22] = queryClient;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  const invalidateGroup = t12;
  let t13;
  if ($[24] !== invalidateGroup) {
    t13 = t14 => {
      const {
        groupId: groupId_0
      } = t14;
      return invalidateGroup({
        groupId: groupId_0
      });
    };
    $[24] = invalidateGroup;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const onGroupRemoved = t13;
  let t14;
  if ($[26] !== invalidateGroup) {
    t14 = t15 => {
      const {
        groupId: groupId_1
      } = t15;
      return invalidateGroup({
        groupId: groupId_1
      });
    };
    $[26] = invalidateGroup;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const onGroupChanged = t14;
  let t15;
  if ($[28] !== queryClient) {
    t15 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GROUPS]
      });
    };
    $[28] = queryClient;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const onGroupCreate = t15;
  let t16;
  if ($[30] !== queryClient) {
    t16 = t17 => {
      const {
        data,
        handleOrWallet
      } = t17;
      const {
        ratings,
        rater: rater_0
      } = data;
      const initialEmptyRepRates = {
        total_rep_rating: ratings.total_rep_rating,
        number_of_raters: ratings.number_of_raters,
        total_rep_rating_by_rater: null,
        rep_rates_left_for_rater: null,
        rating_stats: ratings.rating_stats.map(_temp2)
      };
      queryClient.setQueryData([QueryKey.PROFILE_REP_RATINGS, {
        handleOrWallet,
        rater: undefined
      }], initialEmptyRepRates);
      if (rater_0) {
        queryClient.setQueryData([QueryKey.PROFILE_REP_RATINGS, {
          handleOrWallet,
          rater: rater_0.toLowerCase()
        }], ratings);
      }
    };
    $[30] = queryClient;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const setRepRates = t16;
  let t17;
  if ($[32] !== queryClient) {
    t17 = t18 => {
      const {
        data: data_0,
        params
      } = t18;
      const {
        page,
        pageSize,
        given,
        order,
        orderBy,
        handleOrWallet: handleOrWallet_0,
        matter
      } = params;
      queryClient.setQueryData([QueryKey.PROFILE_RATERS, {
        handleOrWallet: handleOrWallet_0,
        matter,
        page: `${page}`,
        pageSize: `${pageSize}`,
        order,
        orderBy,
        given
      }], data_0);
    };
    $[32] = queryClient;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  const setProfileRaters = t17;
  let t18;
  if ($[34] !== invalidateQueries) {
    t18 = profile_4 => {
      const handles_4 = getHandlesFromProfile(profile_4);
      invalidateQueries({
        key: QueryKey.PROFILE_REP_RATINGS,
        values: handles_4.map(_temp3)
      });
    };
    $[34] = invalidateQueries;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  const invalidateProfileRepRatings = t18;
  let t19;
  if ($[36] !== invalidateQueries) {
    t19 = t20 => {
      const {
        profile: profile_5,
        matter: matter_0,
        given: given_0
      } = t20;
      const handles_5 = getHandlesFromProfile(profile_5);
      invalidateQueries({
        key: QueryKey.PROFILE_RATERS,
        values: handles_5.map(h_1 => ({
          handleOrWallet: h_1,
          matter: matter_0,
          given: given_0
        }))
      });
    };
    $[36] = invalidateQueries;
    $[37] = t19;
  } else {
    t19 = $[37];
  }
  const invalidateProfileRaters = t19;
  let t20;
  if ($[38] !== queryClient) {
    t20 = t21 => {
      const {
        rater: rater_1,
        rater_representative
      } = t21;
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_AVAILABLE_CREDIT, {
          rater: rater_1,
          rater_representative
        }]
      });
    };
    $[38] = queryClient;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const invalidateIdentityAvailableCredit = t20;
  let t21;
  if ($[40] !== invalidateIdentityAvailableCredit || $[41] !== invalidateLogs || $[42] !== invalidateProfile || $[43] !== invalidateProfileRaterCICState || $[44] !== invalidateProfileRaters || $[45] !== invalidateQueries) {
    t21 = t22 => {
      const {
        targetProfile,
        connectedProfile,
        rater: rater_2,
        profileProxy: profileProxy_0
      } = t22;
      invalidateProfile(targetProfile);
      invalidateLogs();
      invalidateProfileRaters({
        profile: targetProfile,
        matter: RateMatter.NIC,
        given: false
      });
      if (connectedProfile) {
        invalidateProfileRaters({
          profile: connectedProfile,
          matter: RateMatter.NIC,
          given: true
        });
      }
      if (rater_2) {
        invalidateProfileRaterCICState({
          profile: targetProfile,
          rater: rater_2.toLowerCase()
        });
      }
      if (profileProxy_0?.created_by?.handle && profileProxy_0.granted_to?.handle) {
        invalidateQueries({
          key: QueryKey.PROFILE,
          values: [profileProxy_0.created_by.handle, profileProxy_0.granted_to.handle]
        });
        invalidateQueries({
          key: QueryKey.PROFILE_RATERS,
          values: [{
            handleOrWallet: profileProxy_0.created_by?.handle,
            matter: RateMatter.NIC,
            given: false
          }, {
            handleOrWallet: profileProxy_0.granted_to?.handle,
            matter: RateMatter.NIC,
            given: false
          }]
        });
        invalidateQueries({
          key: QueryKey.PROFILE_PROFILE_PROXIES,
          values: [{
            handleOrWallet: profileProxy_0.created_by.handle
          }, {
            handleOrWallet: profileProxy_0.granted_to.handle
          }]
        });
      }
      const raterTarget = profileProxy_0?.created_by.handle ?? connectedProfile?.handle ?? null;
      const raterRepresentative = profileProxy_0 ? connectedProfile?.handle ?? null : null;
      if (raterTarget) {
        invalidateIdentityAvailableCredit({
          rater: raterTarget,
          rater_representative: raterRepresentative
        });
      }
    };
    $[40] = invalidateIdentityAvailableCredit;
    $[41] = invalidateLogs;
    $[42] = invalidateProfile;
    $[43] = invalidateProfileRaterCICState;
    $[44] = invalidateProfileRaters;
    $[45] = invalidateQueries;
    $[46] = t21;
  } else {
    t21 = $[46];
  }
  const onProfileCICModify = t21;
  let t22;
  if ($[47] !== invalidateIdentityAvailableCredit || $[48] !== invalidateLogs || $[49] !== invalidateProfile || $[50] !== invalidateProfileRaters || $[51] !== invalidateProfileRepRatings || $[52] !== invalidateQueries) {
    t22 = t23 => {
      const {
        targetProfile: targetProfile_0,
        connectedProfile: connectedProfile_0,
        profileProxy: profileProxy_1
      } = t23;
      invalidateProfile(targetProfile_0);
      invalidateProfileRepRatings(targetProfile_0);
      invalidateLogs();
      invalidateProfileRaters({
        profile: targetProfile_0,
        matter: RateMatter.REP,
        given: false
      });
      if (connectedProfile_0) {
        invalidateProfileRaters({
          profile: connectedProfile_0,
          matter: RateMatter.REP,
          given: true
        });
        invalidateQueries({
          key: QueryKey.PROFILE_REP_RATINGS,
          values: [{
            rater: connectedProfile_0.handle,
            handleOrWallet: targetProfile_0.handle
          }]
        });
      }
      if (profileProxy_1?.created_by?.handle && profileProxy_1.granted_to?.handle) {
        invalidateQueries({
          key: QueryKey.PROFILE,
          values: [profileProxy_1.created_by.handle, profileProxy_1.granted_to.handle]
        });
        invalidateQueries({
          key: QueryKey.PROFILE_RATERS,
          values: [{
            handleOrWallet: profileProxy_1.created_by.handle,
            matter: RateMatter.REP,
            given: false
          }, {
            handleOrWallet: profileProxy_1.granted_to.handle,
            matter: RateMatter.REP,
            given: false
          }]
        });
        invalidateQueries({
          key: QueryKey.PROFILE_REP_RATINGS,
          values: [{
            rater: profileProxy_1.created_by.handle,
            handleOrWallet: targetProfile_0.handle
          }, {
            rater: profileProxy_1.granted_to.handle,
            handleOrWallet: targetProfile_0.handle
          }]
        });
        invalidateQueries({
          key: QueryKey.PROFILE_PROFILE_PROXIES,
          values: [{
            handleOrWallet: profileProxy_1.created_by.handle
          }, {
            handleOrWallet: profileProxy_1.granted_to.handle
          }]
        });
      }
      const raterTarget_0 = profileProxy_1?.created_by.handle ?? connectedProfile_0?.handle ?? null;
      const raterRepresentative_0 = profileProxy_1 ? connectedProfile_0?.handle ?? null : null;
      if (raterTarget_0) {
        invalidateIdentityAvailableCredit({
          rater: raterTarget_0,
          rater_representative: raterRepresentative_0
        });
      }
    };
    $[47] = invalidateIdentityAvailableCredit;
    $[48] = invalidateLogs;
    $[49] = invalidateProfile;
    $[50] = invalidateProfileRaters;
    $[51] = invalidateProfileRepRatings;
    $[52] = invalidateQueries;
    $[53] = t22;
  } else {
    t22 = $[53];
  }
  const onProfileRepModify = t22;
  let t23;
  if ($[54] !== invalidateLogs || $[55] !== invalidateProfile || $[56] !== setProfile) {
    t23 = t24 => {
      const {
        profile: profile_6,
        previousProfile
      } = t24;
      setProfile(profile_6);
      invalidateLogs();
      if (previousProfile) {
        invalidateProfile(previousProfile);
      }
    };
    $[54] = invalidateLogs;
    $[55] = invalidateProfile;
    $[56] = setProfile;
    $[57] = t23;
  } else {
    t23 = $[57];
  }
  const onProfileEdit = t23;
  let t24;
  if ($[58] !== invalidateLogs || $[59] !== invalidateProfileCICStatements) {
    t24 = t25 => {
      const {
        profile: profile_7
      } = t25;
      invalidateProfileCICStatements(profile_7);
      invalidateLogs();
    };
    $[58] = invalidateLogs;
    $[59] = invalidateProfileCICStatements;
    $[60] = t24;
  } else {
    t24 = $[60];
  }
  const onProfileStatementAdd = t24;
  let t25;
  if ($[61] !== invalidateLogs || $[62] !== invalidateProfileCICStatements) {
    t25 = t26 => {
      const {
        profile: profile_8
      } = t26;
      invalidateProfileCICStatements(profile_8);
      invalidateLogs();
    };
    $[61] = invalidateLogs;
    $[62] = invalidateProfileCICStatements;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  const onProfileStatementRemove = t25;
  let t26;
  if ($[64] !== queryClient) {
    t26 = t27 => {
      const {
        params: params_0,
        disableActiveGroup,
        data: data_1
      } = t27;
      queryClient.setQueryData([QueryKey.PROFILE_LOGS, convertActivityLogParams({
        params: params_0,
        disableActiveGroup
      })], data_1);
    };
    $[64] = queryClient;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  const initProfileActivityLogs = t26;
  let t27;
  if ($[66] !== initProfileActivityLogs || $[67] !== setProfile || $[68] !== setProfileRaters || $[69] !== setRepRates) {
    t27 = t28 => {
      const {
        profile: profile_9,
        repRates,
        repLogs,
        repGivenToUsers,
        repReceivedFromUsers,
        handleOrWallet: handleOrWallet_1
      } = t28;
      setProfile(profile_9);
      setRepRates({
        data: repRates,
        handleOrWallet: handleOrWallet_1
      });
      initProfileActivityLogs({
        params: repLogs.params,
        data: repLogs.data,
        disableActiveGroup: true
      });
      setProfileRaters(repGivenToUsers);
      setProfileRaters(repReceivedFromUsers);
    };
    $[66] = initProfileActivityLogs;
    $[67] = setProfile;
    $[68] = setProfileRaters;
    $[69] = setRepRates;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  const initProfileRepPage = t27;
  let t28;
  if ($[71] !== initProfileActivityLogs || $[72] !== setProfile || $[73] !== setProfileRaters) {
    t28 = t29 => {
      const {
        profile: profile_10,
        activityLogs,
        cicGivenToUsers,
        cicReceivedFromUsers
      } = t29;
      setProfile(profile_10);
      initProfileActivityLogs({
        params: activityLogs.params,
        data: activityLogs.data,
        disableActiveGroup: true
      });
      setProfileRaters(cicGivenToUsers);
      setProfileRaters(cicReceivedFromUsers);
    };
    $[71] = initProfileActivityLogs;
    $[72] = setProfile;
    $[73] = setProfileRaters;
    $[74] = t28;
  } else {
    t28 = $[74];
  }
  const initProfileIdentityPage = t28;
  let t29;
  if ($[75] !== initProfileActivityLogs) {
    t29 = t30 => {
      const {
        activityLogs: activityLogs_0
      } = t30;
      initProfileActivityLogs({
        params: activityLogs_0.params,
        data: activityLogs_0.data,
        disableActiveGroup: false
      });
    };
    $[75] = initProfileActivityLogs;
    $[76] = t29;
  } else {
    t29 = $[76];
  }
  const initCommunityActivityPage = t29;
  let t30;
  if ($[77] !== queryClient) {
    t30 = t31 => {
      const {
        drop
      } = t31;
      queryClient.setQueryData([QueryKey.FEED_ITEMS], oldData => {
        if (!oldData?.pages.length) {
          return oldData;
        }
        const pages = JSON.parse(JSON.stringify(oldData.pages));
        const quotedDrops = drop.parts.map(_temp4).filter(_temp5);
        if (quotedDrops.length) {
          const modifiedPages = pages.map(items => {
            const modifiedItems = items.map(item => {
              if (item.type === ApiFeedItemType.DropCreated) {
                const modifiedParts = item.item.parts.map(part_0 => {
                  quotedDrops.find(qd => qd && item.item.id === qd.drop_id && part_0.part_id === qd.drop_part_id);
                  return part_0;
                });
                return {
                  ...item,
                  item: {
                    ...item.item,
                    parts: modifiedParts
                  }
                };
              }
              if (item.type === ApiFeedItemType.DropReplied) {
                const modifiedParts_0 = item.item.reply.parts.map(part_1 => {
                  quotedDrops.find(qd_0 => qd_0 && item.item.reply.id === qd_0.drop_id && part_1.part_id === qd_0.drop_part_id);
                  return part_1;
                });
                return {
                  ...item,
                  item: {
                    ...item.item,
                    reply: {
                      ...item.item.reply,
                      parts: modifiedParts_0
                    }
                  }
                };
              }
              return item;
            });
            return modifiedItems;
          });
          return {
            ...oldData,
            pages: modifiedPages
          };
        }
        return {
          ...oldData,
          pages
        };
      });
    };
    $[77] = queryClient;
    $[78] = t30;
  } else {
    t30 = $[78];
  }
  const increaseFeedItemsDropRedropCount = t30;
  let t31;
  if ($[79] !== queryClient) {
    t31 = t32 => {
      const {
        drop: drop_0
      } = t32;
      queryClient.setQueryData([QueryKey.DROPS, {
        limit: "10",
        context_profile: drop_0.author.handle,
        wave_id: drop_0.wave.id,
        include_replies: "true"
      }], oldData_0 => {
        if (!oldData_0?.pages.length) {
          return oldData_0;
        }
        const pages_0 = JSON.parse(JSON.stringify(oldData_0.pages));
        const quotedDrops_0 = drop_0.parts.map(_temp6).filter(_temp7);
        if (quotedDrops_0.length) {
          const modifiedPages_0 = pages_0.map(items_0 => {
            const modifiedItems_0 = items_0.map(item_0 => {
              const modifiedParts_1 = item_0.parts.map(part_3 => {
                quotedDrops_0.find(qd_1 => qd_1 && item_0.id === qd_1.drop_id && part_3.part_id === qd_1.drop_part_id);
                return part_3;
              });
              return {
                ...item_0,
                parts: modifiedParts_1
              };
            });
            return modifiedItems_0;
          });
          return {
            ...oldData_0,
            pages: modifiedPages_0
          };
        }
        return {
          ...oldData_0,
          pages: pages_0
        };
      });
    };
    $[79] = queryClient;
    $[80] = t31;
  } else {
    t31 = $[80];
  }
  const increaseDropsDropRedropCount = t31;
  let t32;
  if ($[81] !== queryClient) {
    t32 = t33 => {
      const {
        drop: drop_1
      } = t33;
      queryClient.setQueryData([QueryKey.DROP_DISCUSSION, {
        drop_id: drop_1.reply_to?.drop_id,
        drop_part_id: drop_1.reply_to?.drop_part_id,
        sort_direction: "ASC"
      }], oldData_1 => {
        if (!oldData_1?.pages.length) {
          return {
            pageParams: [1],
            pages: [{
              count: 1,
              page: 1,
              next: false,
              data: [drop_1]
            }]
          };
        }
        const pages_1 = JSON.parse(JSON.stringify(oldData_1.pages));
        pages_1.at(-1)?.data.push(drop_1);
        return {
          ...oldData_1,
          pages: pages_1
        };
      });
    };
    $[81] = queryClient;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  const addReplyToDropDiscussion = t32;
  let t33;
  if ($[83] !== queryClient) {
    t33 = t34 => {
      const {
        drop: drop_2
      } = t34;
      queryClient.setQueryData([QueryKey.FEED_ITEMS], oldData_2 => {
        if (!oldData_2?.pages.length) {
          return oldData_2;
        }
        const pages_2 = JSON.parse(JSON.stringify(oldData_2.pages));
        const repliedDrop = drop_2.reply_to;
        if (repliedDrop) {
          const modifiedPages_1 = pages_2.map(items_1 => {
            const modifiedItems_1 = items_1.map(item_1 => {
              if (item_1.type === ApiFeedItemType.DropCreated) {
                const modifiedParts_2 = item_1.item.parts.map(part_4 => {
                  item_1.item.id === repliedDrop.drop_id && part_4.part_id === repliedDrop.drop_part_id;
                  return part_4;
                });
                return {
                  ...item_1,
                  item: {
                    ...item_1.item,
                    parts: modifiedParts_2
                  }
                };
              }
              if (item_1.type === ApiFeedItemType.DropReplied) {
                const modifiedParts_3 = item_1.item.reply.parts.map(part_5 => {
                  item_1.item.reply.id === repliedDrop.drop_id && part_5.part_id === repliedDrop.drop_part_id;
                  return part_5;
                });
                return {
                  ...item_1,
                  item: {
                    ...item_1.item,
                    reply: {
                      ...item_1.item.reply,
                      parts: modifiedParts_3
                    }
                  }
                };
              }
              return item_1;
            });
            return modifiedItems_1;
          });
          return {
            ...oldData_2,
            pages: modifiedPages_1
          };
        }
        return {
          ...oldData_2,
          pages: pages_2
        };
      });
    };
    $[83] = queryClient;
    $[84] = t33;
  } else {
    t33 = $[84];
  }
  const increaseFeedItemsDropDiscussionCount = t33;
  let t34;
  if ($[85] !== addReplyToDropDiscussion || $[86] !== increaseDropsDropRedropCount || $[87] !== increaseFeedItemsDropDiscussionCount || $[88] !== increaseFeedItemsDropRedropCount || $[89] !== queryClient) {
    t34 = async t35 => {
      const {
        drop: drop_3
      } = t35;
      addDropToDrops(queryClient, {
        drop: drop_3
      });
      increaseWavesOverviewDropsCount(queryClient, drop_3.wave.id);
      increaseFeedItemsDropRedropCount({
        drop: drop_3
      });
      increaseDropsDropRedropCount({
        drop: drop_3
      });
      if (drop_3.reply_to) {
        addReplyToDropDiscussion({
          drop: drop_3
        });
        increaseFeedItemsDropDiscussionCount({
          drop: drop_3
        });
      }
    };
    $[85] = addReplyToDropDiscussion;
    $[86] = increaseDropsDropRedropCount;
    $[87] = increaseFeedItemsDropDiscussionCount;
    $[88] = increaseFeedItemsDropRedropCount;
    $[89] = queryClient;
    $[90] = t34;
  } else {
    t34 = $[90];
  }
  const addOptimisticDrop = t34;
  let invalidateAllWaves;
  let invalidateDrops;
  let onIdentityBulkRate;
  let t35;
  let waitAndInvalidateDrops;
  if ($[91] !== queryClient) {
    waitAndInvalidateDrops = async () => {
      await wait(500);
      invalidateDrops();
    };
    onIdentityBulkRate = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_LOGS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_RATERS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_RATER_CIC_STATE]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_AVAILABLE_CREDIT]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_PROFILE_PROXIES]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_PROXY]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_REP_RATINGS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.COMMUNITY_MEMBERS_TOP]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GROUP]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.GROUPS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_PROFILE_PROXIES]
      });
    };
    invalidateAllWaves = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVES_OVERVIEW]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVES_OVERVIEW_PUBLIC]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVES]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVES_PUBLIC]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE_OUTCOMES]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.WAVE_OUTCOME_DISTRIBUTION]
      });
    };
    invalidateDrops = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROPS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROPS_LEADERBOARD]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROP]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.PROFILE_DROPS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.FEED_ITEMS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.DROP_DISCUSSION]
      });
    };
    t35 = () => invalidateAllWaves();
    $[91] = queryClient;
    $[92] = invalidateAllWaves;
    $[93] = invalidateDrops;
    $[94] = onIdentityBulkRate;
    $[95] = t35;
    $[96] = waitAndInvalidateDrops;
  } else {
    invalidateAllWaves = $[92];
    invalidateDrops = $[93];
    onIdentityBulkRate = $[94];
    t35 = $[95];
    waitAndInvalidateDrops = $[96];
  }
  const onWaveCreated = t35;
  let t36;
  if ($[97] !== invalidateAllWaves || $[98] !== queryClient) {
    t36 = t37 => {
      const {
        waveId: waveId_0,
        following
      } = t37;
      toggleWaveFollowing({
        waveId: waveId_0,
        following,
        queryClient
      });
      setTimeout(() => {
        invalidateAllWaves();
      }, 1000);
    };
    $[97] = invalidateAllWaves;
    $[98] = queryClient;
    $[99] = t36;
  } else {
    t36 = $[99];
  }
  const onWaveFollowChange = t36;
  let t37;
  if ($[100] !== queryClient) {
    t37 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_FOLLOWING_ACTIONS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_FOLLOWERS]
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_NOTIFICATIONS]
      });
    };
    $[100] = queryClient;
    $[101] = t37;
  } else {
    t37 = $[101];
  }
  const onIdentityFollowChange = t37;
  let t38;
  if ($[102] !== queryClient) {
    t38 = () => {
      queryClient.invalidateQueries();
    };
    $[102] = queryClient;
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  const invalidateAll = t38;
  let t39;
  if ($[104] !== queryClient) {
    t39 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_NOTIFICATIONS]
      });
    };
    $[104] = queryClient;
    $[105] = t39;
  } else {
    t39 = $[105];
  }
  const invalidateNotifications = t39;
  let t40;
  if ($[106] !== queryClient) {
    t40 = t41 => {
      const {
        identity
      } = t41;
      queryClient.invalidateQueries({
        queryKey: [QueryKey.IDENTITY_TDH_STATS, identity.toLowerCase()]
      });
    };
    $[106] = queryClient;
    $[107] = t40;
  } else {
    t40 = $[107];
  }
  const invalidateIdentityTdhStats = t40;
  let t41;
  if ($[108] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = [QueryKey.FEED_ITEMS];
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  useQueryKeyListener(t41, _temp8);
  let t42;
  if ($[109] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = [QueryKey.IDENTITY_NOTIFICATIONS];
    $[109] = t42;
  } else {
    t42 = $[109];
  }
  useQueryKeyListener(t42, _temp9);
  invalidateDrops;
  let t43;
  if ($[110] !== addOptimisticDrop || $[111] !== initCommunityActivityPage || $[112] !== initProfileIdentityPage || $[113] !== initProfileRepPage || $[114] !== invalidateAll || $[115] !== invalidateDrops || $[116] !== invalidateIdentityTdhStats || $[117] !== invalidateNotifications || $[118] !== onGroupChanged || $[119] !== onGroupCreate || $[120] !== onGroupRemoved || $[121] !== onIdentityBulkRate || $[122] !== onIdentityFollowChange || $[123] !== onProfileCICModify || $[124] !== onProfileEdit || $[125] !== onProfileProxyModify || $[126] !== onProfileRepModify || $[127] !== onProfileStatementAdd || $[128] !== onProfileStatementRemove || $[129] !== onWaveCreated || $[130] !== onWaveFollowChange || $[131] !== setProfile || $[132] !== setProfileProxy || $[133] !== setWave || $[134] !== setWaveDrops || $[135] !== setWavesOverviewPage || $[136] !== waitAndInvalidateDrops) {
    t43 = {
      setProfile,
      setWave,
      setWavesOverviewPage,
      setWaveDrops,
      setProfileProxy,
      onProfileProxyModify,
      onProfileCICModify,
      onProfileRepModify,
      onProfileEdit,
      onProfileStatementAdd,
      onProfileStatementRemove,
      initProfileRepPage,
      initProfileIdentityPage,
      initCommunityActivityPage,
      onGroupRemoved,
      onGroupChanged,
      waitAndInvalidateDrops,
      addOptimisticDrop,
      onIdentityBulkRate,
      onGroupCreate,
      onWaveCreated,
      onWaveFollowChange,
      invalidateAll,
      onIdentityFollowChange,
      invalidateDrops,
      invalidateNotifications,
      invalidateIdentityTdhStats
    };
    $[110] = addOptimisticDrop;
    $[111] = initCommunityActivityPage;
    $[112] = initProfileIdentityPage;
    $[113] = initProfileRepPage;
    $[114] = invalidateAll;
    $[115] = invalidateDrops;
    $[116] = invalidateIdentityTdhStats;
    $[117] = invalidateNotifications;
    $[118] = onGroupChanged;
    $[119] = onGroupCreate;
    $[120] = onGroupRemoved;
    $[121] = onIdentityBulkRate;
    $[122] = onIdentityFollowChange;
    $[123] = onProfileCICModify;
    $[124] = onProfileEdit;
    $[125] = onProfileProxyModify;
    $[126] = onProfileRepModify;
    $[127] = onProfileStatementAdd;
    $[128] = onProfileStatementRemove;
    $[129] = onWaveCreated;
    $[130] = onWaveFollowChange;
    $[131] = setProfile;
    $[132] = setProfileProxy;
    $[133] = setWave;
    $[134] = setWaveDrops;
    $[135] = setWavesOverviewPage;
    $[136] = waitAndInvalidateDrops;
    $[137] = t43;
  } else {
    t43 = $[137];
  }
  const value_0 = t43;
  let t44;
  if ($[138] !== children || $[139] !== value_0) {
    t44 = <ReactQueryWrapperContext.Provider value={value_0}>{children}</ReactQueryWrapperContext.Provider>;
    $[138] = children;
    $[139] = value_0;
    $[140] = t44;
  } else {
    t44 = $[140];
  }
  return t44;
}
function _temp9() {
  Cookies.set([QueryKey.IDENTITY_NOTIFICATIONS].toString(), `${Time.now().toMillis()}`);
}
function _temp8() {
  Cookies.set([QueryKey.FEED_ITEMS].toString(), `${Time.now().toMillis()}`);
}
function _temp7(quotedDrop_0) {
  return !!quotedDrop_0;
}
function _temp6(part_2) {
  return part_2.quoted_drop;
}
function _temp5(quotedDrop) {
  return !!quotedDrop;
}
function _temp4(part) {
  return part.quoted_drop;
}
function _temp3(h_0) {
  return {
    handleOrWallet: h_0
  };
}
function _temp2(rating) {
  return {
    category: rating.category,
    rating: rating.rating,
    contributor_count: rating.contributor_count,
    rater_contribution: 0
  };
}
function _temp(profile) {
  const handles = [];
  if (profile.handle) {
    handles.push(profile.handle.toLowerCase());
  }
  profile.wallets?.forEach(wallet => {
    if (wallet.display) {
      handles.push(wallet.display.toLowerCase());
    }
    handles.push(wallet.wallet.toLowerCase());
  });
  return handles;
}